import {DataObjectId} from './data-object'
import {HubJSON} from './hubs/hub'

export const departmentsContainerId =
  //
  '000048bf-c8ef-460c-9ed4-e773f3991465' as DataObjectId

/**
 * Department object is hosted in `data` field of hubs inside .departments
 * container.
 */
export interface Department {
  id: DataObjectId
  title: string
  managers: DataObjectId[]
  members: DataObjectId[]
  parentId?: DataObjectId
}

export interface EmployeeSettings {
  displayName: string
  position: string
}

export interface DepartmentHierarchy {
  [bossId: string]: {[workerId: string]: boolean}
}

export interface StorageDepartmentHierarchy {
  userTagId?: DataObjectId
  hierarchy?: DepartmentHierarchy
}

export interface DepartmentInfo {
  revision: number
  allDepartments: Department[]
  hierarchy: StorageDepartmentHierarchy
}

export const isDepartmentInsideOf = (
  department: Department | undefined,
  topLevel: Department | undefined,
  all: Department[]
): boolean => {
  if (!topLevel || !department || !department.parentId) {
    return false
  }
  if (topLevel.id === department.id) {
    return false
  }
  if (department.parentId === topLevel.id) {
    return true
  }
  const parent = all.find(d => d.id === department.parentId)
  return isDepartmentInsideOf(parent, topLevel, all)
}

export const getParentDepartments = (
  department: Department,
  all: Department[]
): Department[] => {
  const res: Department[] = []
  let parentId: DataObjectId | undefined = department.parentId
  let depth = 0
  while (parentId && depth < 50) {
    depth++
    const p = all.find(d => d.id === parentId)
    if (p) {
      res.push(p)
    }
    parentId = p ? p.parentId : undefined
  }
  return res
}

export const canSeeAssignedUser = (
  userId: DataObjectId | undefined,
  assigned: DataObjectId | undefined,
  departments: Department[],
  ignoreParentDepartments = false
): boolean => {
  if (!userId || !assigned) {
    return false
  }
  if (userId === assigned) {
    return true
  }
  if (!departments || departments.length === 0) {
    return false
  }
  const userIdIn = (arr: DataObjectId[]) => arr.includes(userId)
  const memberDeps = departments.filter(d => d.members.includes(assigned))
  const managerDeps = departments.filter(d => d.managers.includes(assigned))
  for (const d of memberDeps) {
    if (userIdIn(d.managers)) {
      return true
    }
    if (!ignoreParentDepartments) {
      const parents = getParentDepartments(d, departments)
      for (const p of parents) {
        if (userIdIn(p.managers)) {
          return true
        }
      }
    }
  }
  if (!ignoreParentDepartments) {
    for (const d of managerDeps) {
      const parents = getParentDepartments(d, departments)
      for (const p of parents) {
        if (userIdIn(p.managers)) {
          return true
        }
      }
    }
  }
  return false
}

export const canSeeAssignedUsers = (
  userId: DataObjectId,
  assigned: DataObjectId[],
  departments: Department[],
  ignoreParentDepartments = false
): boolean => {
  if (!assigned) {
    return false
  }
  return !!assigned.find(aid =>
    canSeeAssignedUser(userId, aid, departments, ignoreParentDepartments)
  )
}

export const getStorageHierarchy = (
  userTagId: DataObjectId,
  departments: Department[]
): StorageDepartmentHierarchy => {
  const h: DepartmentHierarchy = {}
  const add = (manager: DataObjectId, member: DataObjectId) => {
    if (manager === member) {
      return
    }
    if (!h[manager]) {
      h[manager] = {}
    }
    h[manager][member] = true
  }
  for (const d of departments) {
    const parentDeps = getParentDepartments(d, departments)
    for (const manager of d.managers) {
      for (const member of d.members) {
        add(manager, member)
      }
    }
    for (const p of parentDeps) {
      for (const pManager of p.managers) {
        for (const manager of d.managers) {
          add(pManager, manager)
        }
        for (const member of d.members) {
          add(pManager, member)
        }
      }
    }
  }
  return {
    userTagId,
    hierarchy: h
  }
}

export const isAvailableForManager = (
  userId: DataObjectId,
  task: {stickers?: HubJSON['stickers']},
  h: StorageDepartmentHierarchy,
  onlyNonDirect = false
): boolean => {
  if (!task?.stickers || !userId || !h?.userTagId || !h?.hierarchy) {
    return false
  }
  let assigned = task.stickers[h.userTagId] as DataObjectId[]
  if (assigned && !(assigned instanceof Array)) {
    assigned = [assigned as any as DataObjectId]
  }
  if (!assigned?.length) {
    return false
  }

  const workers = h.hierarchy[userId] ?? {}
  for (const uid of assigned) {
    const foundWorker = workers[uid]
    const foundSelf = uid === userId && !onlyNonDirect
    if (foundWorker || foundSelf) {
      return true
    }
  }
  return false
}

export const isSameDepartmentUser = (
  userId: DataObjectId,
  task: {stickers?: HubJSON['stickers']},
  userTagId: DataObjectId,
  departments: Department[]
): boolean => {
  if (!task?.stickers || !userId || !userTagId || !departments) {
    return false
  }
  let assigned = task.stickers[userTagId] as DataObjectId[]
  if (assigned && !(assigned instanceof Array)) {
    assigned = [assigned as any as DataObjectId]
  }
  if (!assigned?.length) {
    return false
  }
  const assignedSet = new Set(assigned)
  if (assignedSet.has(userId)) {
    return true
  }

  for (const d of departments) {
    if (d.members.find(id => assignedSet.has(id))) {
      if (d.members.includes(userId) || d.managers.includes(userId)) {
        return true
      }
    }
  }

  return false
}
